// The QR payload of a simplified tax invoice as the ZATCA e-invoicing regulations describe it for phase 1: five TLV
// fields (seller name, VAT registration number, time stamp, invoice total with VAT, VAT total), each a one-byte tag,
// a one-byte length and the UTF-8 value, all base64-encoded. This is the local part only (W-6): phase 2 adds the
// invoice hash, the signature and the public key, which need each organization's onboarding with ZATCA. Nothing here
// has been validated with ZATCA or its SDK; Delta makes no compliance claim (docs/delivery-checklist.md, POS).

export interface QrFields { sellerName: string; vatNumber: string; timestamp: string; total: string; vat: string }

/** Tag, length and value bytes; a value over 255 bytes cannot be encoded and is refused. */
function tlv(tag: number, value: string): number[] {
  const bytes = [...new TextEncoder().encode(value)];
  if (bytes.length > 255) throw new RangeError(`QR field ${tag} is longer than 255 bytes`);
  return [tag, bytes.length, ...bytes];
}

export function zatcaQrBytes(f: QrFields): Uint8Array {
  return Uint8Array.from([...tlv(1, f.sellerName), ...tlv(2, f.vatNumber), ...tlv(3, f.timestamp), ...tlv(4, f.total), ...tlv(5, f.vat)]);
}

export function zatcaQrBase64(f: QrFields): string {
  let s = "";
  for (const b of zatcaQrBytes(f)) s += String.fromCharCode(b);
  return btoa(s);
}

/** Reads the TLV fields back (tests and the receipt check). */
export function readZatcaQr(base64: string): Record<number, string> {
  const raw = atob(base64), bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  const out: Record<number, string> = {};
  for (let i = 0; i < bytes.length;) {
    const tag = bytes[i]!, len = bytes[i + 1]!;
    out[tag] = new TextDecoder().decode(bytes.subarray(i + 2, i + 2 + len));
    i += 2 + len;
  }
  return out;
}

/** Why a phase 1 QR cannot be printed. Delta never fills in a missing or invalid seller detail. */
export type Phase1QrProblem = "seller_name_missing" | "seller_name_too_long" | "vat_number_missing" | "vat_number_invalid" | "timestamp_invalid" | "amount_invalid";
/** A Saudi VAT registration number: 15 digits, first and last 3. */
export const SAUDI_VAT_NUMBER = /^3\d{13}3$/;

/**
 * The phase 1 QR of a sale or sales refund as printed on Delta's documents, or the reasons it cannot be made:
 * - seller name: the organization's Arabic name, else its English name (the same on every print, in either language);
 * - VAT registration number: the organization's, exactly as stored, only when it is a valid Saudi number;
 * - time stamp: the document's date and time, Saudi time, ISO 8601 with its offset ("2026-10-20T10:15:00+03:00");
 * - total with VAT and VAT total: the document's own amounts, two decimals, unchanged.
 * Phase 1 fields only: no hash, signature or key (phase 2, deferred). Not validated with ZATCA or its SDK.
 */
export function phase1Qr(i: { sellerNameAr: string; sellerNameEn: string; vatNumber: string; invoiceAt: string; total: string; vat: string }):
  { ok: true; text: string; fields: QrFields } | { ok: false; problems: Phase1QrProblem[] } {
  const problems: Phase1QrProblem[] = [];
  const sellerName = (i.sellerNameAr || i.sellerNameEn || "").trim(), vatNumber = (i.vatNumber ?? "").trim();
  if (!sellerName) problems.push("seller_name_missing");
  else if (new TextEncoder().encode(sellerName).length > 255) problems.push("seller_name_too_long");
  if (!vatNumber) problems.push("vat_number_missing");
  else if (!SAUDI_VAT_NUMBER.test(vatNumber)) problems.push("vat_number_invalid");
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/.exec(i.invoiceAt ?? "");
  if (!m) problems.push("timestamp_invalid");
  const money = /^-?\d{1,15}\.\d{2}$/;
  if (!money.test(i.total ?? "") || !money.test(i.vat ?? "")) problems.push("amount_invalid");
  if (problems.length) return { ok: false, problems };
  const fields: QrFields = { sellerName, vatNumber, timestamp: `${m![1]}${m![2] ?? ":00"}+03:00`, total: i.total, vat: i.vat };
  return { ok: true, text: zatcaQrBase64(fields), fields };
}

// ---------------------------------------------------------------- e-invoice documents (phase 11b, rules Z-1 to Z-6, provisional)

/** One e-invoice document: a posted sale (388) or sales refund (381), standard (0100000) or simplified (0200000). */
export interface ZatcaDocumentDto {
  id: string; icv: string; uuid: string; typeCode: "388" | "381"; subtype: "0100000" | "0200000"; previousHash: string; invoiceHash: string;
  /** What the document lacks (Z-6), as codes: not_signed, clearance_not_built, seller_vat_number_missing, seller_crn_missing, seller_address_incomplete, buyer_address_incomplete, zero_rate_reason_missing, vat_rounding_per_category. */
  issues: string[]; createdAt: string;
  invoice: { id: string; kind: "sale" | "sale_refund"; number: string; code: string; invoiceAt: string; grand: string; vat: string; partyName: string };
  /** The invoice was reversed in Delta after its document was written (Z-3). */
  reversedInDelta: boolean;
}
export interface ZatcaDocumentsDto { rows: ZatcaDocumentDto[]; unit: { icv: string; lastHash: string } | null }
export interface ZatcaChainDto { documents: number; ok: boolean; problems: { icv: string; problem: "counter_gap" | "previous_hash_mismatch" | "hash_mismatch" | "xml_mismatch" | "unit_mismatch" }[] }
